import { useEffect, useState } from "react";
import { TopBar } from "@/components/PageShell";
import { toast } from "sonner";
import { Copy, Share2, Users, TrendingUp, Gift, Info, ExternalLink, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

const brl = (cents = 0) =>
  `R$ ${((cents || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ label, value, hint, tone = "slate", testid, icon: Icon }) {
  const tones = {
    slate: "text-slate-900",
    green: "text-emerald-700",
    amber: "text-amber-700",
    gold:  "text-[#a37417]",
    rose:  "text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
        {Icon && <Icon className="w-4 h-4 text-slate-400" />}
      </div>
      <div className={`mt-2 text-3xl font-serif ${tones[tone]}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

export default function Referrals() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/referrals/me")
      .then(r => setState(r.data))
      .catch(() => toast.error("Não foi possível carregar suas indicações"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const copy = (text, label = "Link") => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado`);
  };

  const share = async () => {
    if (!state?.link) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Hermes — Advocacia com IA",
          text: `Conheça o Hermes, o app jurídico com IA que uso todo dia. Use meu link:`,
          url: state.link,
        });
      } catch {/* user cancelled */}
    } else {
      copy(state.link, "Link");
    }
  };

  if (loading || !state) {
    return (
      <div className="p-8 text-slate-500" data-testid="referrals-loading">Carregando indicações...</div>
    );
  }

  const noPlan = (state.plan_cap_cents || 0) === 0;

  return (
    <div className="flex-1 min-w-0 flex flex-col" data-testid="referrals-page">
      <TopBar/>
      <div className="flex-1 p-3 sm:p-6 lg:p-8 font-sans">
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a37417]">Financeiro</div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">Minhas Indicações</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Compartilhe seu link com colegas advogados. Quando alguém assinar pelo seu link e pagar a mensalidade,
          você ganha <strong>{brl(state.unit_cents)} de crédito</strong> automaticamente na sua próxima fatura —
          limitado ao valor da sua assinatura naquele mês.
        </p>
      </div>

      {noPlan && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3" data-testid="referrals-no-plan">
          <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
          <div className="text-sm text-amber-900">
            <strong>Ative um plano pago</strong> para começar a ganhar créditos por indicação.
            Você pode continuar compartilhando o link, mas os créditos só serão computados quando sua assinatura estiver ativa.
          </div>
        </div>
      )}

      {/* Link card */}
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-amber-50/40 p-6 shadow-sm" data-testid="referrals-link-card">
        <div className="flex items-center gap-2 mb-3">
          <Gift className="w-4 h-4 text-[#a37417]"/>
          <div className="text-xs uppercase tracking-widest text-[#a37417] font-medium">Seu link exclusivo</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={state.link}
            className="flex-1 h-11 px-4 rounded-lg bg-white border border-slate-200 text-sm font-mono text-slate-800"
            data-testid="referrals-link-input"
          />
          <button
            onClick={() => copy(state.link, "Link")}
            className="h-11 px-4 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 inline-flex items-center gap-2"
            data-testid="referrals-copy-btn"
          >
            <Copy className="w-4 h-4"/> Copiar
          </button>
          <button
            onClick={share}
            className="h-11 px-4 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold inline-flex items-center gap-2"
            data-testid="referrals-share-btn"
          >
            <Share2 className="w-4 h-4"/> Compartilhar
          </button>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Código: <span className="font-mono font-semibold text-slate-700">{state.code}</span>
          {" · "}Você recebe <strong>{brl(state.unit_cents)}</strong> por cada indicado que pagar a mensalidade
          {state.plan_cap_cents > 0 && <> · Limite de {state.max_referrals_per_month} indicações/mês (teto {brl(state.plan_cap_cents)})</>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Kpi
          testid="kpi-clicks"
          label="Cliques no link"
          value={state.clicks}
          hint="Total acumulado"
          icon={TrendingUp}
        />
        <Kpi
          testid="kpi-signups"
          label="Indicados cadastrados"
          value={state.signups}
          hint="Total acumulado"
          icon={Users}
        />
        <Kpi
          testid="kpi-current-credit"
          label={`Crédito em ${state.current_month}`}
          value={brl(state.current_month_credited_cents)}
          hint={state.current_month_pending_cents > 0
            ? `+ ${brl(state.current_month_pending_cents)} aguardando Stripe`
            : "Aplicado na próxima fatura"}
          tone="green"
          icon={CheckCircle2}
        />
        <Kpi
          testid="kpi-remaining"
          label="Restante do teto neste mês"
          value={brl(state.current_month_remaining_cents)}
          hint={state.plan_cap_cents > 0
            ? `Teto: ${brl(state.plan_cap_cents)}`
            : "Ative um plano pago"}
          tone={state.current_month_remaining_cents > 0 ? "gold" : "slate"}
          icon={Gift}
        />
      </div>

      {/* Explainer */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-slate-500 shrink-0 mt-0.5"/>
          <div className="text-sm text-slate-700 space-y-2">
            <div><strong>Como funciona.</strong> Cada indicado que assinar o Hermes pelo seu link e pagar a mensalidade te rende {brl(state.unit_cents)} de crédito no mês corrente.</div>
            <div>O crédito é aplicado automaticamente pelo Stripe na sua <strong>próxima fatura</strong>.</div>
            <div>O total de crédito em um mês nunca pode ultrapassar o valor da sua assinatura naquele mês (ex.: assinatura de {brl(2500)} = até 5 indicações/mês).</div>
            <div>Se você já atingiu o teto do mês, novas indicações continuam contando — mas o crédito virá apenas no mês em que houver espaço no teto.</div>
          </div>
        </div>
      </div>

      {/* Recent activity */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Últimas indicações</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Histórico de créditos</h2>
          </div>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900 underline" data-testid="referrals-refresh">
            Atualizar
          </button>
        </div>
        {(!state.recent || state.recent.length === 0) ? (
          <div className="p-8 text-center text-slate-500 text-sm" data-testid="referrals-empty">
            Nenhum crédito ainda. Compartilhe seu link e comece a ganhar!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-6 py-3">Data</th>
                  <th className="text-left px-6 py-3">Indicado</th>
                  <th className="text-left px-6 py-3">Mês</th>
                  <th className="text-right px-6 py-3">Crédito</th>
                  <th className="text-left px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {state.recent.map(r => {
                  const statusPill = {
                    credited: <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full text-[11px] font-medium"><CheckCircle2 className="w-3 h-3"/>Creditado</span>,
                    pending:  <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full text-[11px] font-medium">Pendente</span>,
                    capped:   <span className="inline-flex items-center gap-1 text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full text-[11px] font-medium">Teto atingido</span>,
                  }[r.status] || <span className="text-slate-500 text-[11px]">{r.status}</span>;
                  const dt = r.created_at ? new Date(r.created_at).toLocaleString("pt-BR", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit"}) : "—";
                  return (
                    <tr key={r.id} data-testid={`referral-row-${r.id}`}>
                      <td className="px-6 py-3 text-slate-600">{dt}</td>
                      <td className="px-6 py-3 text-slate-800">{r.referred_email || "—"}</td>
                      <td className="px-6 py-3 text-slate-600">{r.month}</td>
                      <td className="px-6 py-3 text-right font-medium text-slate-900">
                        {r.status === "capped"
                          ? <span className="text-slate-400 line-through">{brl(r.would_have_credited_cents || 500)}</span>
                          : brl(r.amount_cents)}
                      </td>
                      <td className="px-6 py-3">{statusPill}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
